import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import LoginPassword from './pages/LoginPassword';
import Register from './pages/Register';
import OtpVerification from './pages/OtpVerification';
import AgreementUploadPage from './pages/AgreementUploadPage';
import DashboardBILT from './pages/DashboardBILT';
import PayRent from './pages/PayRent';
import Restaurants from './pages/Restaurants';
import RestaurantDetail from './pages/RestaurantDetail';
import Transactions from './pages/Transactions';
import Redeem from './pages/Redeem';
import RegisterRestaurant from './pages/RegisterRestaurant';
import Verification from './pages/Verification';
import Profile from './pages/Profile';
import MerchantContact from './pages/MerchantContact';
import MerchantBusinessManagement from './pages/MerchantBusinessManagement';
import CompleteProfile from './pages/CompleteProfile';
import DiningEvents from './pages/DiningEvents';
import CreateDiningEvent from './pages/CreateDiningEvent';
import DiningEventDetail from './pages/DiningEventDetail';
import HolaProfileSetup from './pages/HolaProfileSetup';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Feedback from './pages/Feedback';
import AdminFeedback from './pages/AdminFeedback';
import SelectLocation from './pages/SelectLocation';
import FeatureHome from './pages/FeatureHome';
import FeaturePayRent from './pages/FeaturePayRent';
import FeatureNeighborhood from './pages/FeatureNeighborhood';
import FeatureHola from './pages/FeatureHola';
import LocuMerchantDashboard from './pages/LocuMerchantDashboard';
import Redemption from './pages/Redemption';

// Wrapper to add padding for fixed navbar
const PageWrapper = ({ children, showNavbar }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <main className={isHomePage || !showNavbar ? '' : 'pt-16 md:pt-20'}>
      {children}
    </main>
  );
};

function AppContent() {
  const location = useLocation();
  
  // Check if we're on a merchant page - hide global Navbar on merchant pages
  const isMerchantPage = location.pathname.startsWith('/merchant') ||
                         location.pathname.startsWith('/register-restaurant') ||
                         location.pathname === '/merchant-contact' ||
                         location.pathname === '/restaurant/register';
  
  // Check merchant intent from localStorage
  const merchantIntent = typeof window !== 'undefined' ? 
    (localStorage.getItem('merchantIntent') === 'true' || 
     sessionStorage.getItem('isMerchant') === 'true') : false;
  
  // Check if we're on auth pages with merchant intent
  const isAuthPageWithMerchantIntent = (location.pathname === '/login' || 
                                        location.pathname === '/signup' || 
                                        location.pathname === '/otp' ||
                                        location.pathname === '/register') && merchantIntent;
  
  const shouldShowNavbar = !isMerchantPage && !isAuthPageWithMerchantIntent;
  
  return (
    <div className="min-h-screen">
      {shouldShowNavbar && <Navbar />}
      <PageWrapper showNavbar={shouldShowNavbar}>
      <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/feature/home" element={<FeatureHome />} />
            <Route path="/feature/pay-rent" element={<FeaturePayRent />} />
            <Route path="/feature/neighborhood" element={<FeatureNeighborhood />} />
            <Route path="/feature/hola" element={<FeatureHola />} />
            <Route path="/merchant-contact" element={<MerchantContact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login-password" element={<LoginPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Register />} />
            <Route path="/otp" element={<OtpVerification />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/select-location" element={<SelectLocation />} />
            <Route
              path="/agreement-upload"
              element={
                <PrivateRoute>
                  <AgreementUploadPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardBILT />
                </PrivateRoute>
              }
            />
            <Route
              path="/neighborhood"
              element={
                <PrivateRoute>
                  <Restaurants />
                </PrivateRoute>
              }
            />
            <Route
              path="/pay-rent"
              element={
                <PrivateRoute>
                  <PayRent />
                </PrivateRoute>
              }
            />
            <Route path="/restaurants" element={<Restaurants />} />
            <Route
              path="/restaurants/:id"
              element={
                <PrivateRoute>
                  <RestaurantDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/redeem/:restaurantId"
              element={
                <PrivateRoute>
                  <Redeem />
                </PrivateRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <PrivateRoute>
                  <Transactions />
                </PrivateRoute>
              }
            />
            <Route
              path="/redemption"
              element={
                <PrivateRoute>
                  <Redemption />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/register"
              element={
                <PrivateRoute>
                  <RegisterRestaurant />
                </PrivateRoute>
              }
            />
            <Route
              path="/merchant/dashboard"
              element={
                <PrivateRoute>
                  <LocuMerchantDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/merchant/add-business"
              element={
                <PrivateRoute>
                  <RegisterRestaurant />
                </PrivateRoute>
              }
            />
            <Route
              path="/merchant/business/:businessId"
              element={
                <PrivateRoute>
                  <MerchantBusinessManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/verification"
              element={
                <PrivateRoute>
                  <Verification />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/dining-events"
              element={
                <PrivateRoute>
                  <DiningEvents />
                </PrivateRoute>
              }
            />
            <Route
              path="/dining-events/create"
              element={
                <PrivateRoute>
                  <CreateDiningEvent />
                </PrivateRoute>
              }
            />
            <Route
              path="/dining-events/:eventId"
              element={
                <PrivateRoute>
                  <DiningEventDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/hola-profile-setup"
              element={
                <PrivateRoute>
                  <HolaProfileSetup />
                </PrivateRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route
              path="/admin/feedback"
              element={
                <PrivateRoute>
                  <AdminFeedback />
                </PrivateRoute>
              }
            />
          </Routes>
      </PageWrapper>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
        <Analytics />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;

